import type * as React from 'react';
import { useMemo } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import type { Environment } from 'relay-runtime';
import { anonymousEnvironment, protectedEnvironment, publicEnvironment } from './environment';

/**
 * Determines which Relay environment to use based on the route pathname.
 *
 * - anonymousEnvironment (/session/graphql): Fund pages served without a login.
 *   This covers sf/ and gf/ (disclaimer-gated) and gd/ ("guest direct" public
 *   fund links). These fund paths are only resolvable by the session endpoint.
 * - protectedEnvironment (/infoservice/graphql): For infoservice routes.
 * - publicEnvironment (/graphql): Default for content pages and bare direct ISIN
 *   paths like /AT..., which the public endpoint gates via the disclaimer flow.
 *
 * Note: bare direct ISIN paths (e.g. /AT0000A1E853) intentionally use
 * publicEnvironment — the /session/graphql endpoint returns VisitorInfoPage for
 * them, whereas /graphql drives the disclaimer/denied gate. gd/ differs: it is a
 * public fund view and must hit the session endpoint (the public endpoint cannot
 * resolve a gd/ fund path and returns a GraphQL error).
 *
 * @param pathname - The current route pathname
 * @returns The appropriate Relay Environment
 */
export function getEnvironmentForPath(pathname: string): Environment {
  if (pathname.indexOf('sf/') > -1 || pathname.indexOf('gf/') > -1 || pathname.indexOf('gd/') > -1) {
    return anonymousEnvironment;
  }
  if (pathname.indexOf('infoservice') > -1) {
    return protectedEnvironment;
  }
  return publicEnvironment;
}

interface EnvironmentProviderWrapperProps {
  pathname: string;
  children: React.ReactNode;
}

/**
 * Wrapper component that provides the correct Relay environment based on the current route.
 * This enables all child components to access the appropriate environment via React context.
 */
export function EnvironmentProviderWrapper({ pathname, children }: EnvironmentProviderWrapperProps): React.ReactElement {
  const environment = useMemo(() => getEnvironmentForPath(pathname), [pathname]);

  return <RelayEnvironmentProvider environment={environment}>{children}</RelayEnvironmentProvider>;
}

export default EnvironmentProviderWrapper;
